import type { ReactNode } from 'react';
import { Modal as AntModal } from 'antd';
import type { ModalProps as AntModalProps } from 'antd/es/modal';
import { useFela } from 'react-fela';

import { Button, Icon, type ButtonProps } from '@/modules/ui';

import { buttons, container } from './Modal.rules';

const MODAL_WIDTH = 338;

export interface ModalProps {
    open?: boolean;
    width?: number | string;
    zIndex?: number;
    closable?: boolean;
    children?: ReactNode;
    okButton?: ButtonProps;
    onClose?: AntModalProps['onCancel'];
    cancelButton?: ButtonProps;
    alignButtonsRight?: boolean;
    extend?: {
        container?: typeof container;
        buttons?: typeof buttons;
    };
    dataTestId?: string;
}

export function Modal({
    open = false,
    width = MODAL_WIDTH,
    closable = false,
    alignButtonsRight = false,
    children,
    onClose,
    okButton,
    cancelButton,
    extend,
    zIndex,
    dataTestId = 'modal',
}: ModalProps) {
    const { css } = useFela({
        alignButtonsRight,
    });

    return (
        <AntModal
            width={width}
            wrapClassName={css(container, extend?.container)}
            wrapProps={{
                'data-testid': 'modal-overlay',
            }}
            open={open}
            footer={null}
            closable={closable}
            maskClosable={closable}
            onCancel={onClose ?? cancelButton?.onClick}
            closeIcon={<Icon type='x' data-testid='modal-close-icon' />}
            centered
            destroyOnClose
            zIndex={zIndex}
            bodyProps={{
                'data-testid': dataTestId,
            }}
        >
            {children}
            <div className={css(buttons, extend?.buttons)}>
                {cancelButton ? <Button htmlType='button' data-testid='modal-cancel-button' {...cancelButton} /> : null}
                {okButton ? <Button htmlType='button' type='primary' data-testid='modal-confirm-button' {...okButton} /> : null}
            </div>
        </AntModal>
    );
}
